/**
 * Montage — « **Couper dans le rythme** » (SPEC-UI §5.3, avenant A-3 : *rythmer*).
 *
 * Le mini-jeu **signature** du pipeline, et le seul jeu de timing pur qui subsiste après
 * l'avenant A-3. **Une piste musicale accompagne les cuts depuis A-76**, tirée seedée côté
 * serveur depuis C38 (A-161 §3, cette note remplace celle qui disait le contraire) : le clic
 * attendu tombe sur un temps fort de la piste réellement jouée, pas sur un intervalle fixe.
 * Sans musique (silence, mode posé, `prefers-reduced-motion`), le métronome visuel suffit — le
 * beat se voit, les marqueurs défilent vers la ligne de coupe, et on frappe au passage.
 *
 * Jouable **à la souris et au clavier** (Espace ou Entrée) : c'est une exigence du brief.
 * Sous `prefers-reduced-motion`, bascule en **mode posé** (§7.4) : plus de défilement, les cuts
 * se présentent un par un dans un anneau qui se remplit — même barème, fenêtre W = 800.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ⟦C47 · A-168 §2⟧ **Des variantes**, tirées côté SERVEUR (jamais ici) : des cuts *doublés* (deux
 * cuts à un temps d'écart) ou un passage à *contretemps* (les cuts tombent entre les temps). Les
 * cuts restent sur la grille de la piste musicale, c'est la musique qui reste juste, pas la
 * fréquence des frappes. Chaque marqueur porte sa nature à l'écran : le second d'une paire est
 * doublé d'un trait, un contretemps est un losange.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ⟦A-59⟧ **« Le repérage » — la deuxième PHASE qu'une compétence ajoute au jeu**, sur le
 * patron du pilote ⟦A-56⟧ (le plan lumière du Tournage).
 *
 * Le verbe est *repérer* : avant que la piste défile, le joueur pose sur elle **ses propres
 * marqueurs** — le geste du monteur qui dérushe. Ils sont **moins nombreux que les cuts**, donc
 * il faut choisir. Et un marqueur est un **engagement**, pas un cadeau : il élargit la fenêtre
 * du passage qu'il annonce, mais il ne rapporte que si ce passage est réellement tenu.
 * Annoncer un cut facile garantit peu ; annoncer un enchaînement serré rapporte plein pot — si
 * on le tient. Aucune des deux n'est dominante : c'est un compromis, pas un formulaire.
 *
 * ⭐ **Une différence avec le pilote, et elle est structurante** : le plan lumière se notait
 * tout de suite, à la fin de sa phase. Le repérage, lui, **est noté par ce qui suit** — sa note
 * n'existe qu'une fois la piste jouée. L'écran d'entre-deux ne montre donc pas une note : il
 * montre **la promesse** (« 3 passages annoncés, leur fenêtre passe de 180 à 252 ms »).
 *
 * **Sans le nœud, ce fichier joue exactement le Montage d'hier** : `pattern.reperage` vaut
 * `null`, la phase n'existe pas, aucune fenêtre n'est élargie et le score est celui de la piste
 * seule. ⟦A-10⟧ pris au mot — l'état neutre est une absence, pas un réglage à 1,0.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte.** Consigne : une phrase, les touches, les nombres (cuts, BPM,
 * fenêtre, exigence), une ligne par variante ; la règle entière, la variante dite en entier, le repérage,
 * le multicam et la leçon de Matt sont derrière « Les dessous ». PENDANT la piste : un compteur, un chrono,
 * les touches (« Espace · Entrée · clic ») et des retours d'un mot (« pile poil ! », « à côté ») ; jamais
 * une phrase. L'entre-deux du dérushage : le nombre de passages annoncés et leurs fenêtres en chiffres.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MontagePatternsFile } from "@maxime/shared";

import {
  ECHANTILLON_VISE_MS,
  MARGE_VITESSE,
  VITESSE_CADRE_MAX,
  creerHorodateur,
  gestesMax,
  type TimelineMontage,
} from "../../api/atelier";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { useMusique, useSfx } from "../../components/SoundProvider";
import { useUIStore } from "../../stores/uiStore";
import { BlocChiffresDuTour } from "../core/BlocChiffresDuTour";
import { DessousMiniJeu } from "../core/DessousMiniJeu";
import { ligneExigence } from "../core/exigence";
import { NoteSur100 } from "../core/NoteSur100";
import {
  COMMANDES,
  CONSIGNE,
  PLUS_DE_COUPES,
  ligneAnnonces,
  ligneMarqueur,
  ligneMulticam,
  ligneReperage,
} from "../core/textes";
import { useChronoSonore } from "../core/useChronoSonore";
import { SEUIL_PAUSE_MS, useRafLoop } from "../core/useRafLoop";
import { useTremblementSM } from "../core/useTremblementSM";
import type { ProprietesMiniJeu } from "../core/types";
import { CONSIGNES, depuisServeur, genererPattern } from "./pattern";
import { chargerPatternsMontage } from "./patternsCalcules";
import {
  REPERAGE_FENETRE_MERITE,
  REPERAGE_FENETRE_PLANCHER,
  apparier,
  fenetresApresReperage,
  marqueursPoses,
  noter,
  valeurDuCut,
  type Appariement,
  type Hit,
  type OptionsReperage,
  type Piste,
} from "./scoring";

/** Position de la ligne de coupe dans la piste, en % de la largeur. */
const LIGNE_PCT = 22;

/** ⟦A-59⟧ Pas de déplacement d'un marqueur au clavier, en fraction de piste par appui. */
const PAS_CLAVIER = 0.02;

/**
 * ⟦A-76⟧ Au-delà de cette dérive entre la position réelle de la musique et celle que la grille
 * des cuts attend, on COUPE la musique plutôt que de la laisser mentir. Le cas concret : un
 * alt-tab — `useRafLoop` met l'horloge de jeu en pause (`SEUIL_PAUSE_MS`), la carte son, elle,
 * continue. Au retour, chaque temps musical tomberait à côté de chaque cut : *une grille fausse
 * est pire que le silence* (⟦A-71 §6⟧). 100 ms, c'est un peu moins que la demi-fenêtre du
 * palier max (118 ms) : on coupe avant que le mensonge ne coûte un cut.
 */
const DERIVE_MAX_MS = 100;

/**
 * ⟦A-59⟧ **Ce que l'écran appelle « annoncé »** : un cut qui a récolté plus de la moitié du
 * mérite possible. Seuil d'**affichage** — aucun barème ne le lit, il ne sert qu'à décider ce
 * qu'on met en évidence et ce qu'on liste dans l'entre-deux.
 */
const SEUIL_ANNONCE = 1 + REPERAGE_FENETRE_PLANCHER + REPERAGE_FENETRE_MERITE / 2;

/** ⟦A-59⟧ `reperage` et `entre` n'existent que si le nœud a ouvert la phase. */
type Phase = "pret" | "reperage" | "entre" | "jeu" | "fini";

/**
 * ⟦C50⟧ La porte du jeu. En compte, le serveur a tiré le pattern : on joue tout de suite. En bac à sable hors
 * mode posé, le client tire dans le fichier de patterns pré-calculés (`patternsCalcules.ts`, chargé à la
 * demande) : on attend qu'il soit là, ou on dit qu'il n'est pas venu — jamais de carrés inventés en attendant.
 */
export function CouperDansLeRythme(props: ProprietesMiniJeu) {
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const besoinDuFichier = !props.pattern && !reducedMotion;
  const [fichier, setFichier] = useState<MontagePatternsFile | null>(null);
  const [echec, setEchec] = useState(false);
  useEffect(() => {
    if (!besoinDuFichier) return;
    let vivant = true;
    chargerPatternsMontage()
      .then((f) => vivant && setFichier(f))
      .catch(() => vivant && setEchec(true));
    return () => {
      vivant = false;
    };
  }, [besoinDuFichier]);

  if (besoinDuFichier && !fichier) {
    return (
      <div className="jeu jeu-montage">
        <h3 className="jeu-titre">✂️ Couper dans le rythme</h3>
        <p className="jeu-regle">
          {echec ? "Pistes indisponibles : recharge la page." : "Préparation de la piste…"}
        </p>
      </div>
    );
  }
  return <CouperDansLeRythmeJeu {...props} patterns={fichier} />;
}

function CouperDansLeRythmeJeu({
  stage,
  state,
  format,
  seed,
  pattern: servi,
  onFini,
  patterns,
  accueil,
  actionsPlus,
  consequence,
}: ProprietesMiniJeu & { patterns: MontagePatternsFile | null }) {
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const tolerance = useUIStore((s) => s.settings.tolerance);
  /**
   * ⟦A-76⟧ Calage audio manuel (SPEC-UI §7.7) : les hits sont « corrigés de `audioOffsetMs`
   * avant envoi » (§5.3). La correction s'applique à la CAPTURE, pas à la soumission : ainsi le
   * barème local note exactement les instants que le serveur rejouera. Et elle s'applique que la
   * musique joue ou non — c'est une constante du joueur, pas un état de l'écran : la retirer du
   * chemin muet ferait dépendre le score de ce qu'on entend, ce que ⟦A-22⟧ interdit.
   */
  const audioOffsetMs = useUIStore((s) => s.settings.audioOffsetMs);
  const sfx = useSfx();
  const musique = useMusique();
  const tremblement = useTremblementSM(state);

  const pattern = useMemo(
    () =>
      servi
        ? depuisServeur(servi.patterns.montage, state)
        : genererPattern({
            state,
            format,
            seed,
            pose: reducedMotion,
            // Les assists de la branche ✂️ (SPEC-UI §5.3) : lus de l'arbre, appliqués au pattern.
            previewLong: state.skills.includes("sound_design_qui_claque"),
            fenetreBonus: state.skills.includes("jump_cut_sauvage") ? 1.15 : 1,
            tempoMult: state.skills.includes("chapitrage") ? 0.9 : 1,
            multicam: state.skills.includes("multicam"),
            // ⟦A-59⟧ *Chapitrage* porte l'assist `phase_reperage` (content/skills.json).
            phaseReperage: state.skills.includes("chapitrage"),
            // ⟦C50⟧ Bac à sable seulement : le serveur tire déjà (`depuisServeur`). Le client tire dans le
            // fichier de patterns pré-calculés, comme lui : aucun carré n'est calculé ici.
            patterns,
          }),
    [servi, state, format, seed, reducedMotion, patterns],
  );

  /**
   * ⟦A-76 · C38⟧ **La musique du Montage** — les trois gestes d'⟦A-71 §6⟧, dans l'ordre.
   *
   * 1. La piste : **tirée par le pattern** (`server/src/atelier/patterns.ts`, seedée, aucune
   *    répétition entre deux parties d'affilée — A-161 §3) ; `null` en mode posé, et **le silence est un
   *    résultat**, pas une panne (§7.4 : les cuts posés sont à intervalle fixe, aucune musique ne leur
   *    correspond). ⟦C50⟧ La musique n'est plus étirée : chaque carré du pattern est posé sur une attaque
   *    du morceau tel quel.
   * 2. Le départ : au passage `phase === "jeu"` (effet plus bas), dans le même tour que la
   *    remise à zéro de l'horloge.
   * 3. La preuve : son coupé, même score — voir la sonde d'⟦A-76⟧ et `DERIVE_MAX_MS`.
   */
  const piste = pattern.piste;

  // Décodage dès le montage de l'écran : le calage au lancement ne doit pas payer le fetch.
  useEffect(() => {
    if (piste) musique?.precharger(piste);
  }, [piste, musique]);

  const plan = pattern.reperage;
  /** ⟦C47⟧ La variante annoncée avant la piste (`null` = le jeu de base, rien à dire). */
  const consigne = CONSIGNES[pattern.variante];
  const [phase, setPhase] = useState<Phase>("pret");
  const [t, setT] = useState(-pattern.leadInMs);
  /**
   * ⟦C50 · A-169 §2⟧ La dernière image lue : l'horloge de jeu ET l'heure (`performance.now`) à laquelle on l'a
   * lue. Une frappe tombe entre deux images ; lire `t` (l'état React, celui de la dernière image) la datait
   * jusqu'à UNE IMAGE trop tôt (16 ms à 60 Hz, 33 ms à 30 Hz, en moyenne la moitié) — un biais systématique,
   * dans le sens où le joueur est « en avance » alors qu'il est pile. La frappe se date donc depuis l'heure
   * réelle de l'événement, prolongée depuis la dernière image (sans dépasser le seuil de pause : au-delà, le jeu
   * était en pause et l'horloge n'a pas avancé).
   */
  const derniereImage = useRef({ t: -pattern.leadInMs, perf: 0 });
  const [hits, setHits] = useState<Hit[]>([]);
  const hitsRef = useRef<Hit[]>([]);
  const zone = useRef<HTMLDivElement>(null);

  // ── ⟦A-59⟧ « Le repérage » ────────────────────────────────────────────────────────────
  //
  // Comme pour les lampes ⟦A-56⟧, deux positions par marqueur : la *cible* est là où le joueur
  // demande qu'il aille, la *position* est là où il est vraiment — il s'y rend à la vitesse
  // bornée que le serveur tolère. On ne soumet que des commandes.
  const [positions, setPositions] = useState<number[]>(() => [...(plan?.departs ?? [])]);
  const [marqueurActif, setMarqueurActif] = useState(0);
  const [tRep, setTRep] = useState(0);
  const [tlReperage, setTlReperage] = useState<TimelineMontage["reperage"]>(undefined);

  const ciblesRep = useRef<number[]>([...(plan?.departs ?? [])]);
  const positionsRef = useRef<number[]>([...(plan?.departs ?? [])]);
  const dernierRep = useRef(0);
  const posesRep = useRef<NonNullable<TimelineMontage["reperage"]>["poses"]>([]);
  const dernierEchRep = useRef(Number.NEGATIVE_INFINITY);
  const glisse = useRef(false);
  const marqueurActifRef = useRef(0);
  const bande = useRef<HTMLDivElement>(null);

  /**
   * ⚠️ **Deux bornes que le serveur impose à la frappe, et qu'il faut tenir ICI.**
   *
   * 1. `1,25 × nombre de cuts` frappes au maximum : au-delà, la soumission entière part en
   *    `422 invalid_timeline` (§6.4 pas 1) et le joueur perd ses cinq mini-jeux d'un coup.
   *    On refuse donc la frappe en trop, plutôt que de la compter puis de tout perdre.
   * 2. Deux frappes à moins de 80 ms ⇒ l'étape est **neutralisée à 0** (§6.4 pas 2). Les cuts
   *    tombent sur un temps fort tous les deux temps, soit ≥ 980 ms d'écart même à 122 BPM :
   *    aucune frappe utile n'est perdue par cette borne, seuls les doubles-clics le sont.
   */
  const plafondFrappes = gestesMax(pattern.cuts.length);
  const horodateur = useRef(creerHorodateur());

  /**
   * ⟦A-59⟧ Le repérage **figé**, reconstruit depuis la timeline soumise — jamais depuis les
   * positions affichées. C'est la même reconstruction que celle du serveur, et c'est ce qui
   * garantit que le chiffre de l'écran est celui qu'il écrira.
   */
  const planNote: OptionsReperage | null = useMemo(
    () =>
      plan
        ? { rayonMs: plan.rayonMs, marqueurs: marqueursPoses(plan, pattern.durationMs, tlReperage) }
        : null,
    [plan, pattern.durationMs, tlReperage],
  );

  /** La fenêtre de chaque cut, une fois le repérage pris en compte — `null` sans la phase. */
  const fenetres = useMemo(
    () => (planNote ? fenetresApresReperage(pattern.cuts, pattern.windowMs, planNote) : null),
    [planNote, pattern.cuts, pattern.windowMs],
  );

  // Appariement recalculé à chaque frappe : c'est LE barème §5.3, pas une approximation
  // d'affichage — le retour immédiat et le score final ne peuvent donc pas diverger.
  const appariements = useMemo(
    () => apparier(pattern.cuts, hits, pattern.windowMs, fenetres),
    [pattern, hits, fenetres],
  );
  const servis = useMemo(
    () => new Set(appariements.map((a) => a.cut).filter((c): c is number => c !== null)),
    [appariements],
  );
  const dernier: Appariement | undefined = appariements.at(-1);

  // Le son suit l'appariement, pas une approximation : ce qu'on entend est ce qui est compté.
  const nbHits = hits.length;
  useEffect(() => {
    if (nbHits === 0 || !dernier) return;
    const fenetre = (dernier.cut !== null ? fenetres?.[dernier.cut] : null) ?? pattern.windowMs;
    sfx(
      dernier.cut === null
        ? "cut_miss"
        : dernier.mauvaisePiste || valeurDuCut(dernier.delta ?? 0, fenetre) < 100
          ? "cut_hit"
          : "cut_perfect",
    );
    // On ne rejoue pas au re-render : seul un hit de plus déclenche un son.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nbHits]);

  // ── ⟦A-59⟧ La phase de repérage ───────────────────────────────────────────────────────

  /** Fin du repérage : on fige la timeline, et on passe à la piste. */
  const cloreReperage = useCallback(
    (aMs: number) => {
      if (!plan) return;
      const fin = Math.max(0, Math.min(plan.chronoMs, Math.round(aMs)));
      // Un dernier échantillon par marqueur, **à l'instant exact de la fin** : sans lui, le
      // serveur reconstruirait la commande d'il y a une frame et élargirait d'autres fenêtres
      // que celles qu'on vient d'afficher.
      const finales = plan.departs.map((_, i) => ({
        marqueur: i,
        atMs: fin,
        position: positionsRef.current[i]!,
      }));
      setTlReperage({ poses: [...posesRep.current, ...finales], finishedAtMs: fin });
      setPhase("entre");
    },
    [plan],
  );

  useRafLoop(phase === "reperage" && plan !== null, (ecoule) => {
    if (!plan) return;
    const dt = Math.max(0, ecoule - dernierRep.current);
    dernierRep.current = ecoule;
    setTRep(ecoule);

    // Le marqueur rejoint sa cible à la même vitesse bornée que le cadre du Tournage : le
    // serveur applique `VITESSE_CADRE_MAX` aux marqueurs aussi (`marqueur_teleporte`).
    const course = VITESSE_CADRE_MAX * MARGE_VITESSE * (dt / 1000);
    const suivantes = positionsRef.current.map((p, i) => {
      const cible = ciblesRep.current[i]!;
      const d = Math.abs(cible - p);
      if (d <= course || d === 0) return cible;
      return p + Math.sign(cible - p) * course;
    });
    positionsRef.current = suivantes;
    setPositions(suivantes);

    if (ecoule - dernierEchRep.current >= ECHANTILLON_VISE_MS && ecoule <= plan.chronoMs) {
      dernierEchRep.current = ecoule;
      const i = marqueurActifRef.current;
      posesRep.current.push({ marqueur: i, atMs: Math.round(ecoule), position: suivantes[i]! });
    }

    if (ecoule >= plan.chronoMs) cloreReperage(plan.chronoMs);
  });

  useChronoSonore(plan ? plan.chronoMs - tRep : 0, phase === "reperage");

  /** Saisir le marqueur le plus proche du clic, puis le traîner le long de la bande. */
  const fractionDuPointeur = useCallback((clientX: number): number | null => {
    const boite = bande.current?.getBoundingClientRect();
    if (!boite || boite.width === 0) return null;
    return Math.max(0, Math.min(1, (clientX - boite.left) / boite.width));
  }, []);

  const attraper = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!plan) return;
      const x = fractionDuPointeur(e.clientX);
      if (x === null) return;
      let proche = 0;
      let meilleure = Number.POSITIVE_INFINITY;
      positionsRef.current.forEach((p, i) => {
        const d = Math.abs(p - x);
        if (d < meilleure) {
          meilleure = d;
          proche = i;
        }
      });
      marqueurActifRef.current = proche;
      setMarqueurActif(proche);
      glisse.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      ciblesRep.current[proche] = x;
      sfx("cut_hit");
    },
    [plan, fractionDuPointeur, sfx],
  );

  const trainer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!glisse.current) return;
      const x = fractionDuPointeur(e.clientX);
      if (x === null) return;
      ciblesRep.current[marqueurActifRef.current] = x;
    },
    [fractionDuPointeur],
  );

  const lacher = useCallback(() => {
    glisse.current = false;
  }, []);

  // Le clavier du repérage : 1-2-3 choisissent le marqueur, ← → le déplacent.
  useEffect(() => {
    if (phase !== "reperage" || !plan) return;
    const auClavier = (e: KeyboardEvent) => {
      const rang = Number(e.key);
      if (Number.isInteger(rang) && rang >= 1 && rang <= plan.marqueurs) {
        e.preventDefault();
        marqueurActifRef.current = rang - 1;
        setMarqueurActif(rang - 1);
        return;
      }
      const pas = e.key === "ArrowLeft" ? -PAS_CLAVIER : e.key === "ArrowRight" ? PAS_CLAVIER : 0;
      if (pas === 0) return;
      e.preventDefault();
      const i = marqueurActifRef.current;
      ciblesRep.current[i] = Math.max(0, Math.min(1, ciblesRep.current[i]! + pas));
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [phase, plan]);

  /**
   * ⟦A-71 §6 · A-76⟧ La musique démarre **dans le même tour que la remise à zéro de l'horloge** :
   * cet effet et celui de `useRafLoop(phase === "jeu")` s'exécutent dans le même commit React.
   *
   * ⟦C50 · A-169 §2⟧ `caler` fait ENTENDRE `origineMs` (l'instant du fichier qui est le zéro du pattern) à la
   * fin du décompte, donc sur le zéro de la piste : chaque carré, posé sur une attaque du morceau, tombe sur
   * elle. « Entendre » : la latence de sortie mesurée sur la carte son (176 ms sur un casque Bluetooth) est
   * retranchée du départ (`LecteurMusique#jouer`, `latence.ts`) — sans elle, la musique arrivait aux
   * oreilles après les carrés. Le décompte se joue sur la musique qui précède l'origine.
   *
   * Rien n'attend ce départ : `jouer` ne rend rien, ne lève rien, et un morceau qui ne démarre
   * pas (mute, fichier absent, manifeste pas encore là) laisse la piste se jouer au métronome
   * visuel, exactement comme avant ⟦A-76⟧. *Le rythme peut aider, il ne décide jamais.*
   */
  useEffect(() => {
    if (phase !== "jeu" || !piste || !musique) return;
    musique.jouer(piste, {
      volume: 0.5,
      fondueMs: 120,
      caler: { positionMs: pattern.origineMs, dansMs: pattern.leadInMs },
    });
    return () => musique.arreter({ fondueMs: 150 });
  }, [phase, piste, musique, pattern.leadInMs, pattern.origineMs]);

  useRafLoop(phase === "jeu", (ecoule) => {
    const instant = ecoule - pattern.leadInMs;
    derniereImage.current = { t: instant, perf: performance.now() };
    setT(instant);
    // ⟦A-76⟧ La sonde de dérive : si la carte son et l'horloge de jeu ne racontent plus le même
    // temps (alt-tab, gros hoquet), on coupe la musique — voir `DERIVE_MAX_MS`. Elle ne LIT que
    // la musique, elle n'écrit jamais dans le jeu : la piste continue, muette. ⟦C50⟧ On compare la position
    // ENTENDUE (latence de sortie retranchée) à `origineMs + instant`.
    if (instant >= 0 && piste && musique && musique.idEnCours() === piste) {
      const entendue = musique.positionEntendueMs();
      if (entendue !== null && entendue > 0) {
        const attendu = pattern.origineMs + instant;
        if (Math.abs(entendue - attendu) > DERIVE_MAX_MS) musique.arreter({ fondueMs: 120 });
      }
    }
    if (instant >= pattern.durationMs) setPhase("fini");
  });

  const frapper = useCallback(
    (track?: Piste) => {
      if (phase !== "jeu") return;
      // ⟦C50⟧ L'instant de jeu MAINTENANT, prolongé depuis la dernière image (voir `derniereImage`).
      const image = derniereImage.current;
      const ecart = performance.now() - image.perf;
      const maintenant = image.t + (image.perf > 0 && ecart > 0 && ecart < SEUIL_PAUSE_MS ? ecart : 0);
      // Une frappe pendant le décompte ne sert aucun cut. Elle était comptée comme orphelin,
      // donc pénalisée ; elle ne peut plus l'être, parce que la timeline n'a pas d'instant
      // négatif à offrir (`Instant` est borné à ≥ 0 côté serveur). Frapper trop tôt est
      // désormais sans effet — c'est le seul endroit où la couture change une règle, et elle
      // la rend plus douce, jamais plus dure.
      if (maintenant < 0) return;
      if (hitsRef.current.length >= plafondFrappes) return;
      // Hors du `setHits` : sous `StrictMode`, une fonction de mise à jour est appelée deux fois
      // et l'horodateur avancerait deux fois — la frappe suivante serait mangée.
      //
      // ⟦A-76⟧ SPEC-UI §5.3/§7.7 : l'instant capturé est corrigé de `audioOffsetMs` — un joueur
      // dont la chaîne audio retarde de X ms frappe X ms en retard, la correction lui rend son
      // intention. À 0 (défaut, et tant que le panneau Réglages n'expose pas le champ), cette
      // ligne est l'identité au caractère près. Borné à ≥ 0 : la timeline n'a pas d'instant
      // négatif à offrir (`Instant`, côté serveur).
      const atMs = horodateur.current.accepte(Math.max(0, maintenant - audioOffsetMs));
      if (atMs === null) return;
      hitsRef.current = [...hitsRef.current, { atMs, track }];
      setHits(hitsRef.current);
    },
    [phase, plafondFrappes, audioOffsetMs],
  );

  useEffect(() => {
    if (phase !== "jeu") return;
    const auClavier = (e: KeyboardEvent) => {
      // Multicam (§5.3 pas 3) : F = piste A, J = piste B, Espace = piste C à E5.
      const piste: Piste | undefined =
        pattern.trackCount === 1
          ? undefined
          : e.code === "KeyF"
            ? "A"
            : e.code === "KeyJ"
              ? "B"
              : pattern.trackCount === 3 && e.code === "Space"
                ? "C"
                : undefined;
      const touchePiste = pattern.trackCount > 1 && piste !== undefined;
      const toucheSimple =
        pattern.trackCount === 1 && (e.code === "Space" || e.code === "Enter" || e.key === " ");
      if (!touchePiste && !toucheSimple) return;
      e.preventDefault();
      if (e.repeat) return;
      frapper(piste);
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [phase, frapper, pattern.trackCount]);

  useEffect(() => {
    if (phase === "jeu") zone.current?.focus();
  }, [phase]);

  const resultat = useMemo(
    () =>
      noter(
        pattern.cuts,
        appariements,
        pattern.windowMs,
        pattern.scoreCap,
        planNote && fenetres ? { fenetres, plan: planNote } : null,
      ),
    [pattern, appariements, planNote, fenetres],
  );

  // ───────────────────────── Avant de lancer ─────────────────────────
  if (phase === "pret") {
    return (
      <div className="jeu jeu-montage">
        <h3 className="jeu-titre">✂️ Couper dans le rythme</h3>
        {/* ⟦C62 · friction 3⟧ La 1re fois, la réplique de Matt tient la place de la phrase de règle : un seul écran. */}
        {accueil ?? (
          <p className="jeu-regle">
            {pattern.pose ? CONSIGNE.montage.reglePose : CONSIGNE.montage.regle}
          </p>
        )}
        <p className="jeu-regle jeu-commandes">{COMMANDES.montage}</p>
        <ul className="jeu-params">
          <li>
            <strong>{pattern.cuts.length}</strong> cuts à placer
          </li>
          {/* ⟦C62 · friction 7⟧ « 92 BPM », « fenêtre 234 ms », « exigence E0 », « score plafonné à 85 » sont
              derrière « Les dessous » (« Les chiffres du tour »). */}
        </ul>
        {consigne ? (
          <p className="jeu-variante" role="note">
            <span className="jeu-variante-badge">{consigne.badge}</span> {consigne.court}
          </p>
        ) : null}
        {plan ? (
          <p className="jeu-regle jeu-regle-reperage">
            🔖 <strong>Dérushage d'abord</strong> : {ligneReperage(plan.marqueurs, pattern.cuts.length)}
          </p>
        ) : null}
        <div className="jeu-actions">
          {/* ⟦C62 · friction 3⟧ Un seul libellé pour lancer un mini-jeu : « Jouer ». */}
          <button
            className="bouton primaire grand"
            type="button"
            onClick={() => setPhase(plan ? "reperage" : "jeu")}
          >
            Jouer
          </button>
          {actionsPlus}
          {/* ⟦C57 · lot 8⟧ Les phrases d'avant, au mot près : la règle, la variante dite en entier, le
              dérushage, le multicam, puis la leçon de Matt entière (`DessousMiniJeu`). */}
          <DessousMiniJeu stage={stage}>
            <BlocChiffresDuTour
              lignes={[
                ...(pattern.pulsation ? [`Tempo : ${pattern.bpm} BPM`] : []),
                `Fenêtre de frappe : ${Math.round(pattern.windowMs)} ms${tolerance ? " (tolérance)" : ""}`,
                ligneExigence(pattern.k),
                ...(pattern.scoreCap < 100 ? [`Score plafonné à ${pattern.scoreCap}`] : []),
              ]}
            />
            <DessousBloc titre="La règle">
              <p className="dessous-texte">
                {pattern.pose
                  ? "Les cuts se présentent un par un : frappe quand l'anneau est plein. Espace, Entrée ou clic."
                  : "Les marqueurs défilent vers la ligne. Frappe quand ils la touchent : Espace, Entrée ou clic."}
              </p>
            </DessousBloc>
            {consigne ? (
              <DessousBloc titre={consigne.badge}>
                <p className="dessous-texte">{consigne.consigne}</p>
              </DessousBloc>
            ) : null}
            {plan ? (
              <DessousBloc titre="Le dérushage d'abord">
                <p className="dessous-texte">
                  Tu as {plan.marqueurs} marqueur
                  {plan.marqueurs > 1 ? "s" : ""} pour {pattern.cuts.length} cuts : place-les sur les
                  passages que tu veux annoncer. Un passage annoncé se coupe plus large, mais il ne
                  rapporte que si tu le tiens.
                </p>
                <p className="dessous-texte">
                  Traîne un marqueur à la souris, ou <strong>1-2-3</strong> puis <strong>← →</strong>.
                  Un marqueur éclaire son voisinage : posé entre deux cuts, il aide les deux à moitié.
                </p>
              </DessousBloc>
            ) : null}
            {pattern.trackCount > 1 ? (
              <DessousBloc titre="Multicam">
                <p className="dessous-texte">
                  Multicam : F pour la piste A, J pour la piste B
                  {pattern.trackCount === 3 ? ", Espace pour la C" : ""} : la mauvaise touche vaut
                  moitié moins.
                </p>
              </DessousBloc>
            ) : null}
            <DessousBloc titre="En cours de piste">
              <p className="dessous-texte">
                Quand il n'y a plus de coupes disponibles sur la piste, laisse-la finir.
              </p>
            </DessousBloc>
          </DessousMiniJeu>
        </div>
      </div>
    );
  }

  // ── ⟦A-59⟧ La phase de repérage ───────────────────────────────────────────────────────
  if (phase === "reperage" && plan) {
    const restant = Math.max(0, (plan.chronoMs - tRep) / 1000);
    const rayonPct = (plan.rayonMs / pattern.durationMs) * 100;
    // Ce que chaque cut gagnerait si on validait maintenant — de l'affichage, jamais du score.
    const apercu = fenetresApresReperage(pattern.cuts, pattern.windowMs, {
      rayonMs: plan.rayonMs,
      marqueurs: positions.map((p) => p * pattern.durationMs),
    });
    return (
      <div className="jeu jeu-montage" style={tremblement}>
        <div className="montage-entete">
          <span className="montage-compteur">{ligneMarqueur(marqueurActif + 1, plan.marqueurs)}</span>
          <span className="montage-restant">{restant.toFixed(1)} s</span>
        </div>

        <div
          className="piste piste-reperage"
          ref={bande}
          tabIndex={0}
          role="application"
          aria-label="Piste à dérusher — traîne les marqueurs sur les passages à annoncer (souris, ou 1-2-3 puis ← →)"
          onPointerDown={attraper}
          onPointerMove={trainer}
          onPointerUp={lacher}
          onPointerCancel={lacher}
        >
          {pattern.cuts.map((cut, i) => (
            <span
              key={i}
              className={`cut-repere piste-${cut.track ?? "A"} ${cut.type ?? ""} ${apercu[i]! > pattern.windowMs * SEUIL_ANNONCE ? "annonce" : ""}`}
              style={{ left: `${(cut.atMs / pattern.durationMs) * 100}%` }}
              aria-hidden="true"
            />
          ))}
          {positions.map((p, i) => (
            <div key={i}>
              <span
                className="halo-reperage"
                style={{ left: `${p * 100}%`, width: `${rayonPct * 2}%` }}
                aria-hidden="true"
              />
              <span
                className={`drapeau ${i === marqueurActif ? "actif" : ""}`}
                style={{ left: `${p * 100}%` }}
                aria-hidden="true"
              >
                🔖<em>{i + 1}</em>
              </span>
            </div>
          ))}
        </div>

        <div className="jauge-prise" aria-label="Temps de dérushage restant">
          <div
            className="jauge-prise-remplissage"
            style={{ width: `${Math.min(100, (tRep / plan.chronoMs) * 100)}%` }}
          />
        </div>
        <button
          className="bouton primaire grand"
          type="button"
          onClick={() => cloreReperage(tRep)}
        >
          Suivant
        </button>
        <p className="jeu-seuils">{COMMANDES.reperage}</p>
      </div>
    );
  }

  // ── ⟦A-59⟧ L'entre-deux : ce qu'on a promis, pas ce qu'on a gagné ─────────────────────
  //
  // Le plan lumière ⟦A-56⟧ affichait ici une note. Le repérage ne le peut pas : sa note est
  // celle des cuts qu'il annonce, et ils n'ont pas encore été joués. On montre donc l'engagement.
  if (phase === "entre" && plan && fenetres) {
    const annonces = fenetres
      .map((f, i) => ({ i, f, cut: pattern.cuts[i]! }))
      .filter((x) => x.f > pattern.windowMs * SEUIL_ANNONCE)
      .sort((a, b) => b.f - a.f);
    return (
      <div className="jeu jeu-montage">
        <h3 className="jeu-titre">🔖 Piste dérushée</h3>
        {/* ⟦C62 · friction 7⟧ La liste « cut à 3,2 s · fenêtre 480 ms » est derrière « Les dessous » (« Les
            fenêtres », plus bas), qui la portait déjà : l'écran garde la phrase. */}
        <p className="jeu-regle">{ligneAnnonces(annonces.length)}</p>
        <div className="jeu-actions">
          <button className="bouton primaire grand" type="button" onClick={() => setPhase("jeu")}>
            Jouer
          </button>
          {/* ⟦C57 · lot 8⟧ Les phrases d'avant : l'engagement, les fenêtres « au lieu de », la piste vue. */}
          <LesDessous titre="Piste dérushée">
            <DessousBloc titre="Ce que tu as promis">
              <p className="dessous-texte">
                {annonces.length === 0
                  ? "Tu n'as annoncé aucun passage : les marqueurs sont restés dans le vide. La piste reste jouable, mais le dérushage ne rapportera rien."
                  : `${annonces.length} passage${annonces.length > 1 ? "s" : ""} annoncé${annonces.length > 1 ? "s" : ""}. À toi de les tenir.`}
              </p>
            </DessousBloc>
            <DessousBloc titre="Les fenêtres">
              <ul className="dessous-lignes">
                {annonces.slice(0, 4).map(({ i, f, cut }) => (
                  <li className="dessous-ligne" key={i}>
                    <span aria-hidden="true">🔖</span>
                    <span>
                      cut à <strong>{(cut.atMs / 1000).toFixed(1)} s</strong> : fenêtre{" "}
                      <strong>{Math.round(f)} ms</strong> au lieu de {Math.round(pattern.windowMs)}
                    </span>
                  </li>
                ))}
                <li className="dessous-ligne">
                  <span aria-hidden="true">🔖</span>
                  <span>
                    les autres cuts :{" "}
                    <strong>
                      {Math.round(pattern.windowMs * (1 + REPERAGE_FENETRE_PLANCHER))} ms
                    </strong>{" "}
                    (tu as vu la piste)
                  </span>
                </li>
              </ul>
            </DessousBloc>
          </LesDessous>
        </div>
      </div>
    );
  }

  // ───────────────────────── Fin de piste ─────────────────────────
  if (phase === "fini") {
    return (
      <div className="jeu jeu-montage">
        <h3 className="jeu-titre">✂️ Coupé.</h3>
        <NoteSur100 note={resultat.score} consequence={consequence?.(resultat.score)} />
        <ul className="jeu-params">
          <li>
            <strong>{resultat.parfaits}</strong> pile poil
          </li>
          <li>
            <strong>{resultat.justes}</strong> de justesse
          </li>
          <li>
            <strong>{resultat.rates}</strong> ratés
          </li>
          <li>
            <strong>{resultat.orphelins}</strong> à côté
            {resultat.penalite > 0 ? ` (−${resultat.penalite})` : ""}
          </li>
          {resultat.reperage ? (
            <li>
              piste <strong>{resultat.piste}</strong> · repérage{" "}
              <strong>{Math.round(resultat.reperage.score)}</strong> (
              {resultat.reperage.annonces.filter((a) => a.note >= 50).length}/
              {resultat.reperage.annonces.length} annonce
              {resultat.reperage.annonces.length > 1 ? "s" : ""} tenue
              {resultat.reperage.annonces.length > 1 ? "s" : ""})
            </li>
          ) : null}
        </ul>
        <button
          className="bouton primaire grand"
          type="button"
          onClick={() => onFini(resultat.score, { hits, reperage: tlReperage })}
        >
          Suivant
        </button>
      </div>
    );
  }

  // ───────────────────────── En jeu ─────────────────────────
  /** ⟦A-59⟧ La fenêtre du cut qu'un appariement a servi — `windowMs` sans la phase. */
  const fenetreDe = (a: Appariement): number =>
    (a.cut !== null ? fenetres?.[a.cut] : null) ?? pattern.windowMs;

  // ⟦C50⟧ Le décompte compte TOUJOURS 3, 2, 1 : `leadInMs` en fait trois tiers (trois temps de la grille du
  // morceau, ou 2 s sans grille fiable). La pulsation, elle, n'existe que si la grille du morceau est fiable
  // (`pattern.pulsation`) : sans elle, un métronome ne ferait que mentir sur des carrés qui sont sur des
  // attaques, pas sur une grille.
  const decompte = t < 0 ? Math.ceil(-t / (pattern.leadInMs / 3)) : 0;
  const battement = pattern.pulsation ? Math.floor(Math.max(0, t) / pattern.beatMs) : 0;
  const dansLeBeat = pattern.pulsation && (Math.max(0, t) % pattern.beatMs) / pattern.beatMs < 0.18;

  return (
    // ⟦A-76⟧ SPEC-UI §7.5 : en zone rouge de SM, la zone de jeu tremble (±1 px / 280 ms).
    <div className="jeu jeu-montage" style={tremblement}>
      <div className="montage-entete">
        <span className="montage-compteur">
          {servis.size}/{pattern.cuts.length} cuts
        </span>
        {pattern.pulsation ? (
          <span className={`metronome ${dansLeBeat ? "tape" : ""}`} aria-hidden="true">
            ●
          </span>
        ) : (
          <span aria-hidden="true" />
        )}
        <span className="montage-restant">
          {Math.max(0, Math.ceil((pattern.durationMs - t) / 1000))} s
        </span>
      </div>

      <div
        className={`piste ${pattern.pose ? "piste-posee" : ""}`}
        ref={zone}
        tabIndex={0}
        role="button"
        aria-label="Zone de frappe — Espace, Entrée ou clic pour couper"
        onPointerDown={(e) => {
          e.preventDefault();
          // En multicam, le clic sert la piste A : le clavier reste le vrai contrôle.
          frapper(pattern.trackCount > 1 ? "A" : undefined);
        }}
      >
        {pattern.pose ? (
          <AnneauPose pattern={pattern} t={t} />
        ) : (
          <>
            <div className="ligne-de-coupe" style={{ left: `${LIGNE_PCT}%` }} />
            {pattern.cuts.map((cut, i) => {
              const restant = cut.atMs - t;
              // ⟦A-59⟧ Chaque cut vit sa propre fenêtre : celle qu'un marqueur lui a achetée,
              // ou `windowMs` — c'est-à-dire exactement l'ancien code quand la phase manque.
              const fenetre = fenetres?.[i] ?? pattern.windowMs;
              if (restant > pattern.previewMs || restant < -fenetre) return null;
              const part = restant / pattern.previewMs;
              const x = LIGNE_PCT + part * (100 - LIGNE_PCT);
              return (
                <div
                  key={i}
                  className={`marqueur piste-${cut.track ?? "A"} ${cut.type ?? ""} ${servis.has(i) ? "servi" : ""} ${restant < -fenetre / 2 ? "manque" : ""} ${fenetre > pattern.windowMs * SEUIL_ANNONCE ? "annonce" : ""}`}
                  style={{ left: `${x}%`, top: positionPiste(cut.track, pattern.trackCount) }}
                >
                  {pattern.trackCount > 1 ? (
                    <span className="marqueur-touche">
                      {cut.track === "A" ? "F" : cut.track === "B" ? "J" : "␣"}
                    </span>
                  ) : null}
                </div>
              );
            })}
            {/* Le métronome visuel : une pulsation sur chaque temps, pour tenir le tempo. */}
            {pattern.pulsation ? (
              <div className={`pulsation ${dansLeBeat ? "tape" : ""}`} key={battement} />
            ) : null}
          </>
        )}

        {decompte > 0 ? <div className="decompte">{decompte}</div> : null}

        {dernier && Math.abs(dernier.hitMs - t) < 420 ? (
          <div className={`retour ${classeRetour(dernier, fenetreDe(dernier))}`}>
            {libelleRetour(dernier, fenetreDe(dernier))}
          </div>
        ) : null}
      </div>

      {/* ⟦C57 · lot 8⟧ Des touches, jamais une phrase : la règle entière et le multicam sont dans les dessous
          de la consigne. */}
      <p className="jeu-seuils">
        {hits.length >= plafondFrappes
          ? PLUS_DE_COUPES
          : pattern.trackCount > 1
            ? ligneMulticam(pattern.trackCount)
            : COMMANDES.montage}
      </p>
    </div>
  );
}

function classeRetour(a: Appariement, windowMs: number): string {
  if (a.cut === null || a.delta === null) return "rate";
  if (a.mauvaisePiste) return "juste";
  return valeurDuCut(a.delta, windowMs) >= 100 ? "parfait" : "juste";
}

function libelleRetour(a: Appariement, windowMs: number): string {
  if (a.cut === null || a.delta === null) return "à côté";
  if (a.mauvaisePiste) return "mauvaise caméra";
  if (valeurDuCut(a.delta, windowMs) >= 100) return "pile poil !";
  return a.delta < 0 ? "un poil tôt" : "un poil tard";
}

/** En multicam, chaque piste a sa hauteur dans le couloir — sinon on ne lit rien. */
function positionPiste(track: Piste | undefined, trackCount: number): string {
  if (trackCount === 1 || !track) return "50%";
  const rangs: Record<Piste, number> = { A: 0, B: 1, C: 2 };
  return `${(100 / (trackCount + 1)) * (rangs[track] + 1)}%`;
}

/** Mode posé (§7.4) : un anneau qui se remplit, on frappe quand il est plein. */
function AnneauPose({
  pattern,
  t,
}: {
  pattern: ReturnType<typeof genererPattern>;
  t: number;
}) {
  const prochain = pattern.cuts.find((c) => c.atMs > t - pattern.windowMs / 2);
  if (!prochain) return null;
  const debut = prochain.atMs - pattern.windowMs;
  const part = Math.max(0, Math.min(1, (t - debut) / pattern.windowMs));
  return (
    <div className="anneau" style={{ ["--progression" as string]: `${part * 360}deg` }}>
      <span className="anneau-centre">{part >= 0.94 ? "COUPE" : "…"}</span>
    </div>
  );
}
